import { parseLocalISODate, formatLocalISODate } from '@/lib/dateUtils';

interface DateCellProps {
  value: unknown;
}

/**
 * Displays the date through the SAME native `<input type="date">` mask the
 * editor (`DateCellEditor`) uses — but read-only — so the format the user sees
 * at rest is byte-for-byte identical to the format they see once the cell is
 * being edited.
 *
 * Why not render plain text via `toLocaleDateString`? The native date input's
 * visible mask follows the OS *regional format* setting, which is NOT exposed
 * to JavaScript and can differ from `navigator.language` (the locale `Intl`
 * falls back to when you pass `undefined`). When the two disagree — a very
 * common setup, e.g. UI language `en-US` but regional format UK/European — the
 * text display reads `06/18/2026` while the picker reads `18/06/2026`, so the
 * field you think is the day is actually the month. Rendering an actual native
 * date input is the only way to guarantee the closed and open states agree,
 * the same way the shared `DatePicker` stays self-consistent by always mounting
 * one native input.
 */
export function DateCell({ value }: DateCellProps) {
  if (value == null || value === '') return null;
  const date = parseLocalISODate(String(value));
  // Non-ISO values (e.g. timestamptz strings) aren't representable in a native
  // date input — fall back to the raw text, as before.
  if (!date) return <span>{String(value)}</span>;
  return (
    <input
      type="date"
      value={formatLocalISODate(date) ?? ''}
      readOnly
      tabIndex={-1}
      className="cell-date-display text-xs font-mono"
    />
  );
}
